import React, { useEffect, useState } from 'react'
import { Boards } from "../../src/BoardList"
import MetaTags  from "../../src/MetaTags"
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { listBoards } from '../../src/Firebase'
import LayoutFirebase from "../../src/LayoutFirebase"
import InspirationsSearch, {searchClient} from "../../src/InspirationsSearch"
import { findResultsState } from 'react-instantsearch-dom/server';
import RoomCategoryLinkList from "../../src/RoomCategoryLinkList"

const styles = [
  "Contemporary",
  "Modern",
  "Traditional",
  "Rustic",
  "Minimalist",
  "Industrial",
  "Coastal",
  "Bohemian",
  "Scandinavian",
  "Mid-Century Modern",
  "Art Deco",
  "French Country",
  "Shabby Chic",
  "Hollywood Regency",
  "Tropical",
  "Farmhouse",
  "Craftsman",
  "Gothic",
  "Asian Zen",
  "Southwestern",
  "Victorian",
  "Transitional",
  "Mediterranean",
  "Moroccan",
  "Eclectic",
  "Nautical"
]
const roomTypes = [
  "Living Room",
  "Dining Room",
  "Bedroom",
  "Kitchen",
  "Bathroom",
  "Home Office",
  "Nursery",
  "Kids Room",
  "Yoga Studio",
  "Garden",
  "Library",
  "Theater",
  "Studio",
  "Game Room",
  "Wine Cellar",
  "Music Room",
  "Fitness Room",
  "Pantry",
  "Laundry Room",
  "Mudroom",
  "Balcony",
  "Attic",
  "Basement",
  "Closet",
  "Library" 
]
const colorCombinations = [
  "White",
  "Light Grey",
  "Dark Grey",
  "Beige",
  "Light Brown",
  "Black",
  "Red",
  "Off-White",
  "Navy Blue",
  "Light Pink",
  "Light Blue",
  "Light Green",
  "Lavender", 
  "Mint Green",
  "Light Yellow",
  "Peach",
  "Light Orange",
  "Light Purple",
  "Yellow",
  "Orange",
  "Pink",
  "Blue",
  "Green",
  "Purple", 
  "Magenta",
  "Tan",
  "Ivory",
  "Gold",
  "Powder Blue",
  "Soft Pink",
  "Teal",
  "Coral",
  "Mauve",
  "Rust",
  "Terra Cotta",
  "Mustard Yellow",
  "Slate Blue",
  "Grey", 
  "Charcoal Grey",
  "Silver",
  "Dove Grey",
  "Rose",
  "Blush",
  "Burnt Orange",
  "Forest Green",
  "Dusty Rose"
]
const normalizeString = (str) => str.toLowerCase().replace(/[-_\s]+/g, ' ').trim();

function categoriesIntoSearchQuery(categories) {
  let roomType = null;
  let style = null;
  let colorCombination = null;
  let unmatchedCategories = [];

  categories.forEach(category => {
    const normalizedCategory = normalizeString(category);
    
    const matchedRoomType = roomTypes.find(type => normalizeString(type) === normalizedCategory);
    const matchedStyle = styles.find(s => normalizeString(s) === normalizedCategory);
    const matchedColor = colorCombinations.find(c => normalizeString(c) === normalizedCategory);

    if (matchedRoomType) {
      roomType = matchedRoomType;
    } else if (matchedStyle) {
      style = matchedStyle;
    } else if (matchedColor) {
      colorCombination = matchedColor;
    } else {
      // Fallback: capitalize each word, preserving multi-word categories
      unmatchedCategories.push(
        category.split(/[-_]+/)
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
      );
    }
  });

  return {
    query: unmatchedCategories.join(', '),
    menu: {
      type: "Interior Design AI",
      roomType,
      colorCombination,
      style,
    }
  };
}

const Index = ({ resultsState: resultsStateJSON, searchState: searchStateProp }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { categories } = router.query;
  const [searchState, setSearchState] = useState(searchStateProp)

  const resultsState = JSON.parse(resultsStateJSON)

  useEffect(() => {
    if (!categories) return
    const newSearchState = categoriesIntoSearchQuery(categories)
    
    if (JSON.stringify(newSearchState) !== JSON.stringify(searchState)) {
      setSearchState(newSearchState)
    }
  }, [categories, searchState])

  // Extract the dynamic parts
  const { query, menu } = searchState || {};

  // Create dynamic title and description with AI room designer focus
  const titleParts = [
    menu?.roomType,
    menu?.style,
    menu?.colorCombination,
    ...(query ? query.split(', ') : [])
  ].filter(Boolean);

  const title = titleParts.length
    ? `AI Interior Room Designer: ${titleParts.join(', ')} Ideas`
    : "AI Interior Room Designer - Generate Stunning Room Designs";

  const description = titleParts.length
    ? `Transform your ${titleParts.join(', ')} with our AI interior room designer. Get instant AI-generated room design ideas and inspiration for ${titleParts.join(' and ')} spaces. Free AI-powered interior design solutions.`
    : 'Use our AI interior room designer to create stunning interior designs instantly. Generate thousands of AI-powered room design ideas for free. Transform any space with artificial intelligence interior design.';

  const imageUrl = resultsState.rawResults[0].hits[0]
    ? resultsState.rawResults[0].hits[0].image
    : "/assets/MoodBoardly-maroccan-style.jpg";

  return (
    <>
      <MetaTags
        title={title}
        description={description}
        image={imageUrl}
        video="/assets/MoodBoardly-maroccan-style.mp4"
        videoWidth="2038"
        videoHeight="1528"
        hasTranslation={false}
      />
      <LayoutFirebase>
        <div className="container px-5 pt-24 mx-auto">
          <div className="flex flex-col text-center w-full mb-20">
            <h1 className="font-serif sm:text-5xl text-4xl font-medium mb-6 text-gray-900">
              {title}
            </h1>
          </div>
        </div>
        <InspirationsSearch 
          resultsState={resultsState} 
          searchState={searchState} 
          facetFilters={[['type:"Interior Design AI"']]} 
          baseUrl="/interior-ai-room-designer" 
          imageBaseUrl="/interior-design-ai/b/"
          searchPlaceholder="Find AI room designs"
          showCreateButton={false}
        />
        <RoomCategoryLinkList 
          roomTypes={roomTypes}
          styles={styles}
          baseUrl="/interior-ai-room-designer"
          title="Browse Our Gallery"
          description="Explore our collection of AI-generated interior designs. Get inspired by thousands of beautiful room transformations."
          popularStylesPerRoom={4}
        />
      </LayoutFirebase>
    </>
  );
};
export default Index;

export const getStaticProps = async ({ locale, params }) => {
  const categories = params.categories || []
  
  const searchStateCategories = categoriesIntoSearchQuery(categories)
  const searchState = {
    ...searchStateCategories,
    configure: {
      facetFilters: [['type:"Interior Design AI"']],
    },
  }

  const resultsState = await findResultsState(InspirationsSearch, {
    searchClient,
    indexName: "inspirations",
    searchState,
  });

  return {
    props: {
      ...(await serverSideTranslations(locale)),
      resultsState: JSON.stringify(resultsState),
      searchState,
    },
    revalidate: 3600, //hourly
  }
};


const cleanUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  return url.toLowerCase().replace(/[\s-]+/g, '-');
};

const generateCombinations = (arr1, arr2, arr3) => {
  const result = [];
  if (!arr1 || !arr2 || !arr3) return result;
  
  for (const item1 of arr1) {
    if (!item1) continue;
    for (const item2 of arr2) {
      if (!item2) continue;
      for (const item3 of arr3) {
        if (!item3) continue;
        result.push([cleanUrl(item1), cleanUrl(item2), cleanUrl(item3)]);
      }
    }
  }
  return result;
};

export const getStaticPaths = async () => {
  const combinations = generateCombinations(
    roomTypes,
    colorCombinations,
    styles,
  );

  const paths = combinations.map(combination => {
    return {
      params: {
        categories: combination,
      },
    };
  });

  return {
    paths: ["/interior-ai-room-designer", ...paths.slice(0, process.env.STATIC_LIMIT)],
    fallback: "blocking",
  };
} 